import * as React from "react";
import {
  Activity,
  Ban,
  BellRing,
  CalendarClock,
  CircleCheck,
  CirclePause,
  CirclePlay,
  FilePlus2,
  Flag,
  IndianRupee,
  Link2,
  Mail,
  MailX,
  Pencil,
  ReceiptText,
  Repeat,
  Send,
  Settings2,
  TriangleAlert,
  Undo2,
  Users,
} from "Components/ui/icons";

import { Button } from "Components/ui";
import { BillingActivity, BillingStatus } from "api/billing";
import { cn } from "utils/cn";
import {
  describeOffsetDays,
  formatDate,
  formatDateTime,
  formatINR,
  formatRelative,
  humanize,
} from "utils/format";
import {
  BILLING_STATUS_META,
  ENFORCEMENT_LABELS,
  PAYMENT_METHOD_LABELS,
} from "Routes/Billing/Components/StatusChips";

// Tone names predate the design tokens; they are kept so describeActivity's result type stays the
// same, and each maps to a semantic token pair here.
type Tone = "gray" | "blue" | "green" | "yellow" | "red" | "indigo";

const TONE_CLASSES: Record<Tone, string> = {
  gray: "bg-surface-muted text-muted-foreground",
  blue: "bg-info-subtle text-info-text",
  green: "bg-success-subtle text-success-text",
  yellow: "bg-warning-subtle text-warning-text",
  red: "bg-danger-subtle text-danger-text",
  indigo: "bg-primary-subtle text-primary-text",
};

interface Described {
  icon: React.ReactNode;
  tone: Tone;
  title: string;
  detail?: string | null;
}

type Data = Record<string, unknown> | null;

const str = (data: Data, key: string): string | null => {
  const value = data ? data[key] : undefined;
  return typeof value === "string" && value ? value : null;
};

const num = (data: Data, key: string): number | null => {
  const value = data ? data[key] : undefined;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};

const obj = (data: Data, key: string): Data => {
  const value = data ? data[key] : undefined;
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
};

const strList = (data: Data, key: string): string[] => {
  const value = data ? data[key] : undefined;
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
};

const iconClass = "h-4 w-4";

const SCHEDULE_VERBS: Record<string, string> = {
  schedule_created: "added",
  schedule_updated: "changed",
  schedule_paused: "paused",
  schedule_resumed: "resumed",
  schedule_ended: "ended",
};

const statusLabel = (value: string | null) =>
  value && value in BILLING_STATUS_META
    ? BILLING_STATUS_META[value as BillingStatus].label
    : humanize(value);

const enforcementLabel = (value: string | null) =>
  value === "off" || value === "warn_only" || value === "read_only"
    ? ENFORCEMENT_LABELS[value]
    : humanize(value);

const invoiceLabel = (data: Data) => str(data, "number") || str(data, "invoiceNumber") || "invoice";
const CHANNEL_NAMES: Record<string, string> = { email: "email", whatsapp: "WhatsApp" };

/** " by WhatsApp and email" from data.channels (older entries were email only). */
const viaChannels = (data: Data): string => {
  const names = strList(data, "channels").map((channel) => CHANNEL_NAMES[channel] || channel);
  return ` by ${names.length > 0 ? names.join(" and ") : "email"}`;
};

const receiptNumber = (data: Data): string => {
  const number = str(data, "number");
  return number ? ` ${number}` : "";
};

/** Turns a timeline entry into an icon, tone and sentence. */
export const describeActivity = (entry: BillingActivity): Described => {
  const data = entry.data;
  const amount = num(data, "amount");
  const reason = str(data, "reason");
  switch (entry.type) {
    case "policy_changed": {
      const before = str(obj(data, "before"), "enforcement");
      const after = str(obj(data, "after"), "enforcement");
      return {
        icon: <Settings2 className={iconClass} />,
        tone: "indigo",
        title: "Billing rules updated",
        detail:
          before && after && before !== after
            ? `Enforcement: ${enforcementLabel(before)} → ${enforcementLabel(after)}`
            : null,
      };
    }
    case "contacts_changed":
      return {
        icon: <Users className={iconClass} />,
        tone: "indigo",
        title: "Billing contacts updated",
        detail: strList(data, "contacts").join(", ") || "No contacts",
      };
    case "reminder_settings_changed":
      return {
        icon: <BellRing className={iconClass} />,
        tone: "indigo",
        title: "Reminder settings updated",
      };
    case "override_set":
      return {
        icon: <CirclePause className={iconClass} />,
        tone: "blue",
        title: `Enforcement paused through ${formatDate(str(data, "pausedUntil"))}`,
        detail: reason,
      };
    case "override_cleared":
      return {
        icon: <CirclePlay className={iconClass} />,
        tone: "blue",
        title: "Enforcement pause ended",
        detail: reason,
      };
    case "schedule_created":
    case "schedule_updated":
    case "schedule_paused":
    case "schedule_resumed":
    case "schedule_ended": {
      const verb = SCHEDULE_VERBS[entry.type] || "changed";
      return {
        icon: <Repeat className={iconClass} />,
        tone: entry.type === "schedule_ended" ? "gray" : "indigo",
        title: `Recurring charge “${str(data, "name") || "charge"}” ${verb}`,
        detail:
          str(data, "reason") === "end_date_reached" ? "End date reached" : str(data, "summary"),
      };
    }
    case "invoice_created":
      return {
        icon: <FilePlus2 className={iconClass} />,
        tone: "gray",
        title: `Draft invoice created${
          num(data, "total") !== null ? ` for ${formatINR(num(data, "total"))}` : ""
        }`,
        detail: [
          str(data, "title"),
          str(data, "dueDate") ? `due ${formatDate(str(data, "dueDate"))}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "invoice_updated":
      return {
        icon: <Pencil className={iconClass} />,
        tone: "gray",
        title: "Draft invoice edited",
      };
    case "invoice_issued":
      return {
        icon: <Send className={iconClass} />,
        tone: "blue",
        title: `Invoice ${invoiceLabel(data)} issued${
          num(data, "total") !== null ? ` for ${formatINR(num(data, "total"))}` : ""
        }`,
        detail: [
          str(data, "scheduleName"),
          str(data, "dueDate") ? `due ${formatDate(str(data, "dueDate"))}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "invoice_voided":
      return {
        icon: <Ban className={iconClass} />,
        tone: "gray",
        title: `Invoice ${invoiceLabel(data)} voided`,
        detail: reason,
      };
    case "invoice_due_extended":
      return {
        icon: <CalendarClock className={iconClass} />,
        tone: "yellow",
        title: `Due date of ${invoiceLabel(data)} moved from ${formatDate(
          str(data, "from")
        )} to ${formatDate(str(data, "to"))}`,
        detail: reason,
      };
    case "invoice_review_resolved":
      return {
        icon: <Flag className={iconClass} />,
        tone: "green",
        title: `Review cleared on ${invoiceLabel(data)}`,
        detail: reason,
      };
    case "payment_received": {
      const method = str(data, "method");
      const flagged = data ? data.needsReview === true : false;
      return {
        icon: <IndianRupee className={iconClass} />,
        tone: flagged ? "yellow" : "green",
        title: `${formatINR(amount)} received${
          method ? ` by ${PAYMENT_METHOD_LABELS[method] || humanize(method)}` : ""
        }${str(data, "invoiceNumber") ? ` for ${str(data, "invoiceNumber")}` : ""}`,
        detail: flagged
          ? `Flagged for review: ${str(data, "reviewNote") || "check the amount"}`
          : str(data, "reference") || str(data, "providerPaymentId"),
      };
    }
    case "payment_reversed":
      return {
        icon: <Undo2 className={iconClass} />,
        tone: "red",
        title: `Payment of ${formatINR(amount)} reversed${
          str(data, "invoiceNumber") ? ` on ${str(data, "invoiceNumber")}` : ""
        }`,
        detail: reason,
      };
    case "payment_link_created":
      return {
        icon: <Link2 className={iconClass} />,
        tone: "gray",
        title: `Client opened checkout (${formatINR(amount)})`,
        detail: str(data, "referenceId"),
      };
    case "reminder_sent": {
      const offset = num(data, "offsetDays");
      const manual = data ? data.manual === true : false;
      return {
        icon: <Mail className={iconClass} />,
        tone: "blue",
        title: manual
          ? `Reminder sent manually${viaChannels(data)}`
          : `Reminder sent${viaChannels(data)}${
              offset !== null ? ` (${describeOffsetDays(offset)})` : ""
            }`,
        detail: strList(data, "recipients").join(", "),
      };
    }
    case "reminder_failed":
      return {
        icon: <MailX className={iconClass} />,
        tone: "red",
        title: "Reminder could not be sent",
        detail: [
          str(data, "error"),
          data && data.final === false ? "Will retry automatically" : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "state_changed":
      return {
        icon: <Activity className={iconClass} />,
        tone: str(data, "to") === "read_only" ? "red" : "gray",
        title: `Status changed: ${statusLabel(str(data, "from"))} → ${statusLabel(
          str(data, "to")
        )}`,
        detail: num(data, "amountDue") ? `${formatINR(num(data, "amountDue"))} due` : null,
      };
    case "receipt_sent":
      return {
        icon: <ReceiptText className={iconClass} />,
        tone: "green",
        title: `Receipt${receiptNumber(data)} sent`,
        detail: strList(data, "recipients").join(", "),
      };
    case "receipt_failed":
      return {
        icon: <ReceiptText className={iconClass} />,
        tone: "red",
        title: `Receipt${receiptNumber(data)} could not be sent`,
        detail: [
          str(data, "error"),
          data && data.final === true ? "No more retries" : "Will retry automatically",
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "reconcile_healed":
      return {
        icon: <CircleCheck className={iconClass} />,
        tone: "gray",
        title: `Nightly check corrected the paid amount on ${invoiceLabel(data)}`,
        detail: null,
      };
    case "reconcile_mismatch":
      return {
        icon: <TriangleAlert className={iconClass} />,
        tone: "yellow",
        title: `Payment total mismatch on ${invoiceLabel(data)}`,
        detail: `Recorded ${formatINR(num(data, "amountPaid"))}, payments add up to ${formatINR(
          num(data, "expected")
        )}`,
      };
    default:
      return {
        icon: <Activity className={iconClass} />,
        tone: "gray",
        title: humanize(entry.type),
      };
  }
};

interface ActivityFeedProps {
  entries: BillingActivity[];
  onOpenInvoice?: (invoiceId: string) => void;
  emptyText?: string;
  limit?: number;
}

const ActivityFeed: React.FC<ActivityFeedProps> = ({
  entries,
  onOpenInvoice,
  emptyText = "Nothing has happened yet.",
  limit,
}) => {
  const [expanded, setExpanded] = React.useState<boolean>(false);
  if (entries.length === 0) {
    return <p className="py-4 text-sm text-muted-foreground">{emptyText}</p>;
  }
  const visible = limit && !expanded ? entries.slice(0, limit) : entries;
  return (
    <div>
      <ol className="space-y-4">
        {visible.map((entry) => {
          const described = describeActivity(entry);
          const invoiceId = entry.refs.invoice;
          const actor = entry.actor === "system" ? "System" : entry.actorName || "Admin";
          return (
            <li key={entry._id} className="flex gap-3">
              <span
                className={cn(
                  "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                  TONE_CLASSES[described.tone]
                )}
                aria-hidden="true"
              >
                {described.icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-foreground">
                  {onOpenInvoice && invoiceId ? (
                    <Button
                      variant="link"
                      onClick={() => onOpenInvoice(invoiceId)}
                      className="whitespace-normal text-left"
                    >
                      {described.title}
                    </Button>
                  ) : (
                    described.title
                  )}
                </p>
                {described.detail && (
                  <p className="mt-0.5 break-words text-xs text-muted-foreground">
                    {described.detail}
                  </p>
                )}
                <p
                  className="mt-0.5 text-xs text-subtle-foreground"
                  title={formatDateTime(entry.at)}
                >
                  {actor} · {formatRelative(entry.at)}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
      {limit && entries.length > limit && (
        <Button variant="link" onClick={() => setExpanded((value) => !value)} className="mt-4">
          {expanded ? "Show less" : `Show all ${entries.length}`}
        </Button>
      )}
    </div>
  );
};

export default ActivityFeed;
